// src/pages/sessions/components/LiveSession/whiteboard/DetachedWhiteboardPage.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WhiteboardCanvas from './WhiteboardCanvas';
import WhiteboardBottomBar from './WhiteboardBottomBar';
import { useWhiteboardBroadcastChannel } from './useWhiteboardBroadcastChannel';

/** Reviver for Fabric.js v6 loadFromJSON: (serializedObj, instance) => void */
function fabricReviver(serializedObj, instance) {
  if (serializedObj.objectId) instance.objectId = serializedObj.objectId;
  if (serializedObj.authorIdentity) instance.authorIdentity = serializedObj.authorIdentity;
}

/** Post-load fixup: restore objectId/authorIdentity from source JSON by index */
function restoreCustomProps(canvas, sourceJSON) {
  if (!canvas || !sourceJSON) return;
  const objects = canvas.getObjects();
  const sourceObjects = (typeof sourceJSON === 'string' ? JSON.parse(sourceJSON) : sourceJSON).objects || [];
  for (let i = 0; i < objects.length && i < sourceObjects.length; i++) {
    if (sourceObjects[i].objectId && !objects[i].objectId) {
      objects[i].objectId = sourceObjects[i].objectId;
    }
    if (sourceObjects[i].authorIdentity && !objects[i].authorIdentity) {
      objects[i].authorIdentity = sourceObjects[i].authorIdentity;
    }
  }
}

// Mirror of WhiteboardManager's default settings
const DEFAULT_TOOL_SETTINGS = {
  select: { color: '#000000', size: 4 },
  marker: { color: '#000000', size: 4 },
  brush: { color: '#000000', size: 4 },
  highlighter: { color: '#FFFF00', size: 8 },
  eraser: { color: '#000000', size: 4 },
  line: { color: '#000000', size: 4 },
  arrow: { color: '#000000', size: 4 },
  rectangle: { color: '#000000', size: 4 },
  circle: { color: '#000000', size: 4 },
  filledRectangle: { color: '#000000', size: 4 },
  filledCircle: { color: '#000000', size: 4 },
  text: { color: '#000000', size: 4 },
};

/**
 * Standalone full-screen whiteboard page opened in a separate browser tab.
 * 
 * This component does NOT connect to LiveKit. It communicates with the main
 * session tab via BroadcastChannel. The main tab relays all messages via LiveKit.
 * 
 * Flow:
 *   Teacher draws here → BroadcastChannel → Main tab → LiveKit → Students
 *   Student draws → LiveKit → Main tab → BroadcastChannel → Rendered here
 */
const DetachedWhiteboardPage = () => {
  const { sessionId } = useParams();
  const { t } = useTranslation();
  
  // Tool state
  const [activeTool, setActiveTool] = useState('marker');
  const [toolSettings, setToolSettings] = useState(DEFAULT_TOOL_SETTINGS);
  const [studentDrawingEnabled, setStudentDrawingEnabled] = useState(false);
  const [connected, setConnected] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  
  // Canvas refs
  const fabricCanvasRef = useRef(null);
  const isApplyingRemoteRef = useRef(0);
  const objectsByIdRef = useRef(new Map());
  const pendingMessagesRef = useRef([]); // Queue for messages arriving before canvas is ready
  const liveStrokesRef = useRef(new Map()); // Track in-progress strokes from remote participants
  
  // Ref for sendMessage (resolved after hook returns — avoids circular dependency)
  const sendMessageRef = useRef(null);

  // Detect when Fabric canvas has mounted
  useEffect(() => {
    if (canvasReady) return;
    const check = setInterval(() => {
      if (fabricCanvasRef.current) {
        setCanvasReady(true);
        clearInterval(check);
      }
    }, 50);
    return () => clearInterval(check);
  }, [canvasReady]);

  const generateObjectId = useCallback(() => {
    return `obj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }, []);

  // === Remote object creation helper ===
  const addRemoteObject = useCallback((canvas, objectId, fabricJSON) => {
    if (!canvas || objectsByIdRef.current.has(objectId)) return;
    
    isApplyingRemoteRef.current++;
    const fabric = canvas._fabricModule;
    if (!fabric) {
      isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
      return;
    }
    
    const addObj = (obj) => {
      if (!obj) {
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        return;
      }
      obj.objectId = objectId;
      obj.selectable = true;
      obj.evented = true;
      canvas.add(obj);
      objectsByIdRef.current.set(objectId, obj);
      canvas.renderAll();
      isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
    };
    
    try {
      const json = fabricJSON;
      if (json.type === 'path' && fabric.Path) {
        addObj(new fabric.Path(json.path, { ...json, path: undefined }));
      } else if (json.type === 'rect' && fabric.Rect) {
        addObj(new fabric.Rect(json));
      } else if (json.type === 'circle' && fabric.Circle) {
        addObj(new fabric.Circle(json));
      } else if (json.type === 'line' && fabric.Line) {
        addObj(new fabric.Line([json.x1, json.y1, json.x2, json.y2], json));
      } else if (json.type === 'textbox' && fabric.Textbox) {
        addObj(new fabric.Textbox(json.text || '', json));
      } else if (json.type === 'text' && fabric.Text) {
        addObj(new fabric.Text(json.text || '', json));
      } else if (json.type === 'i-text' && fabric.IText) {
        addObj(new fabric.IText(json.text || '', json));
      } else if (json.type === 'group' && fabric.Group && fabric.util?.enlivenObjects) {
        const result = fabric.util.enlivenObjects(json.objects || []);
        if (result && typeof result.then === 'function') {
          result.then(children => addObj(new fabric.Group(children, { ...json, objects: undefined })))
            .catch(() => { isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1); });
        } else {
          fabric.util.enlivenObjects(json.objects || [], (children) => {
            addObj(new fabric.Group(children, { ...json, objects: undefined }));
          });
        }
      } else if (json.type === 'image' && fabric.Image?.fromURL && json.src) {
        const result = fabric.Image.fromURL(json.src, (img) => {
          if (img) { img.set(json); addObj(img); }
          else { isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1); }
        });
        if (result && typeof result.then === 'function') {
          result.then(img => {
            if (img) { img.set(json); addObj(img); }
            else { isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1); }
          }).catch(() => { isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1); });
        }
      } else if (fabric.util?.enlivenObjects) {
        const result = fabric.util.enlivenObjects([json]);
        if (result && typeof result.then === 'function') {
          result.then(objects => addObj(objects?.[0]))
            .catch(() => { isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1); });
        } else {
          fabric.util.enlivenObjects([json], (objects) => addObj(objects?.[0]));
        }
      } else {
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
      }
    } catch (e) {
      console.error('[DetachedWB] addRemoteObject error:', e);
      isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
    }
  }, []);

  // === Message handler from main tab (uses sendMessageRef to avoid circular dep) ===
  const handleMessageFromMain = useCallback((type, payload) => {
    const send = sendMessageRef.current;
    
    // Messages that don't need the canvas can be processed immediately
    const canvasIndependentTypes = ['whiteboard_closed', 'student_drawing_changed'];
    
    // Queue canvas-dependent messages if canvas isn't ready yet
    if (!canvasIndependentTypes.includes(type) && !fabricCanvasRef.current) {
      pendingMessagesRef.current.push({ type, payload });
      return;
    }
    
    switch (type) {
      case 'canvas_sync': {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        isApplyingRemoteRef.current++;
        const syncJSON = payload.canvasJSON;
        let syncDone = false;
        const onDone = () => {
          if (syncDone) return;
          syncDone = true;
          
          // Force-restore custom properties from source JSON (safety net)
          restoreCustomProps(canvas, syncJSON);
          
          objectsByIdRef.current.clear();
          canvas.getObjects().forEach(obj => {
            if (obj.objectId) objectsByIdRef.current.set(obj.objectId, obj);
          });
          canvas.renderAll();
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
          
          // Aggressive render loop: ResizeObserver / CSS layout can clear the
          // canvas bitmap after our initial renderAll. Keep repainting until stable.
          let renderCount = 0;
          const renderTimer = setInterval(() => {
            const c = fabricCanvasRef.current;
            if (c) c.requestRenderAll();
            renderCount++;
            if (renderCount >= 40) clearInterval(renderTimer); // 40 * 50ms = 2s
          }, 50);
        };
        // Fabric.js v6: loadFromJSON(json, reviver?) → Promise
        try {
          canvas.loadFromJSON(syncJSON, fabricReviver)
            .then(onDone)
            .catch(() => {
              isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
            });
        } catch (e) {
          console.error('[DetachedWB] canvas_sync error:', e);
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        }
        if (payload.studentDrawingEnabled !== undefined) {
          setStudentDrawingEnabled(payload.studentDrawingEnabled);
        }
        setConnected(true);
        break;
      }

      case 'remote_object_added':
        addRemoteObject(fabricCanvasRef.current, payload.objectId, payload.fabricJSON);
        break;

      case 'remote_object_modified': {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const obj = objectsByIdRef.current.get(payload.objectId);
        if (!obj) return;
        isApplyingRemoteRef.current++;
        obj.set(payload.transform);
        if (obj.initDimensions) obj.initDimensions();
        obj.setCoords();
        canvas.renderAll();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }

      case 'remote_object_removed': {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const obj = objectsByIdRef.current.get(payload.objectId);
        if (!obj) return;
        isApplyingRemoteRef.current++;
        canvas.remove(obj);
        objectsByIdRef.current.delete(payload.objectId);
        canvas.renderAll();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }

      case 'canvas_cleared': {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        isApplyingRemoteRef.current++;
        canvas.clear();
        canvas.backgroundColor = '#ffffff';
        objectsByIdRef.current.clear();
        liveStrokesRef.current.clear();
        canvas.renderAll();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }

      // === Live stroke preview from remote participants ===
      case 'remote_stroke_start': {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const fabric = canvas._fabricModule;
        if (!fabric) return;
        
        const { strokeId, point, color, width, opacity } = payload;
        const tempLine = new fabric.Polyline([{ x: point.x, y: point.y }], {
          stroke: color,
          strokeWidth: width,
          fill: null,
          opacity: opacity || 1,
          strokeLineCap: 'round',
          strokeLineJoin: 'round',
          selectable: false,
          evented: false,
          objectCaching: false,
        });
        tempLine._isLiveStroke = true;
        tempLine._strokeId = strokeId;
        canvas.add(tempLine);
        liveStrokesRef.current.set(strokeId, tempLine);
        canvas.renderAll();
        break;
      }

      case 'remote_stroke_point': {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const { strokeId, point } = payload;
        const tempLine = liveStrokesRef.current.get(strokeId);
        if (!tempLine || !tempLine.points) return;
        tempLine.points.push({ x: point.x, y: point.y });
        if (typeof tempLine._setPositionDimensions === 'function') {
          tempLine._setPositionDimensions({});
        }
        tempLine.setCoords();
        tempLine.dirty = true;
        canvas.requestRenderAll();
        break;
      }

      case 'remote_stroke_end': {
        const { strokeId } = payload;
        const tempLine = liveStrokesRef.current.get(strokeId);
        if (tempLine) {
          const canvas = fabricCanvasRef.current;
          if (canvas) {
            canvas.remove(tempLine);
            canvas.renderAll();
          }
          liveStrokesRef.current.delete(strokeId);
        }
        break;
      }

      case 'whiteboard_closed':
        window.close();
        break;

      case 'student_drawing_changed':
        setStudentDrawingEnabled(payload.enabled);
        break;

      // Main tab needs canvas state (for student sync request)
      case 'request_canvas_for_sync': {
        if (send) {
          const canvas = fabricCanvasRef.current;
          const canvasJSON = canvas
            ? canvas.toJSON(['objectId', 'authorIdentity'])
            : { version: '5.3.0', objects: [] };
          send('sync_response', { canvasJSON });
        }
        break;
      }

      // Main tab requesting snapshot before reattaching
      case 'request_snapshot_and_close': {
        if (send) {
          const canvas = fabricCanvasRef.current;
          const canvasJSON = canvas
            ? canvas.toJSON(['objectId', 'authorIdentity'])
            : { version: '5.3.0', objects: [] };
          send('canvas_snapshot', { canvasJSON });
        }
        setTimeout(() => window.close(), 150);
        break;
      }

      default:
        break;
    }
  }, [addRemoteObject]);

  // BroadcastChannel connection
  const { sendMessage, peerConnected } = useWhiteboardBroadcastChannel({
    sessionId,
    role: 'detached',
    onMessage: handleMessageFromMain,
  });

  // Wire up sendMessage ref for use in message handler
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // Request initial canvas state when peer connects AND canvas is ready
  useEffect(() => {
    if (peerConnected && canvasReady) {
      // Process any messages that were queued before canvas was ready
      if (pendingMessagesRef.current.length > 0) {
        const queued = pendingMessagesRef.current.splice(0);
        queued.forEach(({ type, payload }) => handleMessageFromMain(type, payload));
      }
      // Now request the full sync
      sendMessage('request_sync', {});
    }
  }, [peerConnected, canvasReady, sendMessage, handleMessageFromMain]);

  // === Drawing event handlers → forward to main tab ===

  const handlePathCreated = useCallback((path) => {
    if (path.objectId) return; // Already registered (remote)
    const objectId = generateObjectId();
    path.objectId = objectId;
    objectsByIdRef.current.set(objectId, path);
    const fabricJSON = path.toJSON(['objectId', 'authorIdentity']);
    sendMessage('drawing_path_created', { objectId, fabricJSON });
  }, [sendMessage, generateObjectId]);

  const handleObjectModified = useCallback((obj, newTransform) => {
    if (!obj.objectId) return;
    sendMessage('drawing_object_modified', { objectId: obj.objectId, transform: newTransform });
  }, [sendMessage]);

  const handleSelectionChange = useCallback(() => {}, []);

  const handleStrokeStart = useCallback((data) => {
    sendMessage('drawing_stroke_start', data);
  }, [sendMessage]);

  const handleStrokePoint = useCallback((data) => {
    sendMessage('drawing_stroke_point', data);
  }, [sendMessage]);

  const handleStrokeEnd = useCallback((data) => {
    sendMessage('drawing_stroke_end', data);
  }, [sendMessage]);

  const handleShapeCreated = useCallback((shape) => {
    if (shape.objectId) return; // Already registered (remote)
    const objectId = generateObjectId();
    shape.objectId = objectId;
    objectsByIdRef.current.set(objectId, shape);
    const fabricJSON = shape.toJSON(['objectId', 'authorIdentity']);
    sendMessage('drawing_shape_created', { objectId, fabricJSON });
  }, [sendMessage, generateObjectId]);

  const handleTextCreated = useCallback((textbox) => {
    if (textbox.objectId) return; // Already registered (remote)
    const objectId = generateObjectId();
    textbox.objectId = objectId;
    objectsByIdRef.current.set(objectId, textbox);
    const fabricJSON = textbox.toJSON(['objectId', 'authorIdentity']);
    sendMessage('drawing_text_created', { objectId, fabricJSON });
  }, [sendMessage, generateObjectId]);

  const eraserRenderPendingRef = useRef(false);
  const handleObjectErased = useCallback((obj) => {
    if (!obj) return;
    
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    // Remove from canvas
    canvas.remove(obj);
    
    // If the object has an objectId, broadcast and clean up tracking
    if (obj.objectId) {
      objectsByIdRef.current.delete(obj.objectId);
      sendMessage('drawing_object_erased', { objectId: obj.objectId });
    }
    
    // Debounced render for rapid erasing
    if (!eraserRenderPendingRef.current) {
      eraserRenderPendingRef.current = true;
      requestAnimationFrame(() => {
        eraserRenderPendingRef.current = false;
        if (fabricCanvasRef.current) {
          fabricCanvasRef.current.renderAll();
        }
      });
    }
  }, [sendMessage]);

  const handleAddImage = useCallback((base64Data, width, height, posX, posY) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const imgElement = new Image();
    imgElement.onload = () => {
      const fabric = canvas._fabricModule;
      if (!fabric) return;
      const fabricImage = new fabric.Image(imgElement, {
        left: posX ?? 100,
        top: posY ?? 100,
        scaleX: Math.min(1, 400 / width),
        scaleY: Math.min(1, 400 / height),
        selectable: true,
        evented: true,
      });
      const objectId = generateObjectId();
      fabricImage.objectId = objectId;
      canvas.add(fabricImage);
      canvas.setActiveObject(fabricImage);
      canvas.renderAll();
      objectsByIdRef.current.set(objectId, fabricImage);
      const fabricJSON = fabricImage.toJSON(['objectId', 'authorIdentity']);
      sendMessage('drawing_image_added', { objectId, fabricJSON });
    };
    imgElement.src = base64Data;
  }, [sendMessage, generateObjectId]);

  const handleDeleteSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;
    const objects = active.type === 'activeSelection' ? active.getObjects() : [active];
    objects.forEach(obj => {
      if (obj.objectId) {
        sendMessage('drawing_object_erased', { objectId: obj.objectId });
        objectsByIdRef.current.delete(obj.objectId);
      }
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
  }, [sendMessage]);

  // === Toolbar actions ===

  const handleClear = useCallback(() => {
    sendMessage('drawing_clear', {});
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      canvas.clear();
      canvas.backgroundColor = '#ffffff';
      objectsByIdRef.current.clear();
      canvas.renderAll();
    }
  }, [sendMessage]);

  const handleClose = useCallback(() => {
    sendMessage('drawing_close_whiteboard', {});
    window.close();
  }, [sendMessage]);

  const handleAttach = useCallback(() => {
    sendMessage('drawing_reattach_request', {});
  }, [sendMessage]);

  const handleToggleStudentDrawing = useCallback((enabled) => {
    setStudentDrawingEnabled(enabled);
    sendMessage('drawing_toggle_student_drawing', { enabled });
  }, [sendMessage]);

  const updateToolSettings = useCallback((tool, updates) => {
    setToolSettings(prev => ({ ...prev, [tool]: { ...prev[tool], ...updates } }));
  }, []);

  const currentSettings = toolSettings[activeTool] || { color: '#000000', size: 4 };

  // Periodic snapshot — ensures main tab always has recent canvas state
  // even if beforeunload fails when the detached window is closed
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      const send = sendMessageRef.current;
      const canvas = fabricCanvasRef.current;
      if (send && canvas) {
        try {
          send('canvas_snapshot', {
            canvasJSON: canvas.toJSON(['objectId', 'authorIdentity']),
          });
        } catch (e) { /* best effort */ }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [connected]);

  // Snapshot canvas on unload (best-effort for reattach)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const send = sendMessageRef.current;
      if (!send) return;
      try {
        const canvas = fabricCanvasRef.current;
        if (canvas) {
          send('canvas_snapshot', {
            canvasJSON: canvas.toJSON(['objectId', 'authorIdentity']),
          });
        }
        send('detached_closing', {});
      } catch (e) { /* best effort */ }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Set document title
  useEffect(() => {
    document.title = t('whiteboard.detached.title', 'SimpleClass — Whiteboard');
  }, [t]);

  return (
    <div className="h-screen w-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* Connection status banner */}
      {!connected && (
        <div className="bg-amber-600 text-white text-center py-2 text-sm flex-shrink-0">
          {peerConnected
            ? t('whiteboard.detached.syncing', 'Syncing whiteboard...')
            : t('whiteboard.detached.waiting', 'Waiting for session connection... Keep the session tab open.')
          }
        </div>
      )}

      {/* Canvas */}
      <div className="flex-1 overflow-hidden">
        <WhiteboardCanvas
          fabricRef={fabricCanvasRef}
          activeTool={activeTool}
          activeColor={currentSettings.color}
          brushSize={currentSettings.size}
          canDraw={true}
          isTeacher={true}
          onPathCreated={handlePathCreated}
          onObjectModified={handleObjectModified}
          onSelectionChange={handleSelectionChange}
          onStrokeStart={handleStrokeStart}
          onStrokePoint={handleStrokePoint}
          onStrokeEnd={handleStrokeEnd}
          onShapeCreated={handleShapeCreated}
          onTextCreated={handleTextCreated}
          onObjectErased={handleObjectErased}
          onToolChange={setActiveTool}
          onDeleteSelected={handleDeleteSelected}
          onAddImage={handleAddImage}
        />
      </div>

      {/* Toolbar */}
      <WhiteboardBottomBar
        activeTool={activeTool}
        toolSettings={toolSettings}
        isTeacher={true}
        canDraw={true}
        studentDrawingEnabled={studentDrawingEnabled}
        onToolChange={setActiveTool}
        onToolSettingsChange={updateToolSettings}
        onClear={handleClear}
        onClose={handleClose}
        onToggleStudentDrawing={handleToggleStudentDrawing}
        isDetached={true}
        onAttach={handleAttach}
      />
    </div>
  );
};

export default DetachedWhiteboardPage;
